import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faMoneyBill } from '@fortawesome/pro-regular-svg-icons';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';
import Button from 'components/UI/V3/Button/Button';
import { DefaultHeader } from 'components/UI/V3/DefaultHeader/DefaultHeader';
import GenericModal from 'components/UI/V3/Modals/GenericModal/GenericModal';
import { Switch } from 'components/UI/V3/Forms/Switch';
import Spinner from 'components/UI/V3/Spinner';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useApiClientWrapper from 'hooks/useApiClientWrapper';
import { useHasPremiumPlan } from 'hooks/premiumPlan';
import PremiumFeatureAlert from 'components/UI/PremiumFeatureAlert';
import {
    fetchLoyaltyConfigThunk,
    fetchLoyaltySummaryThunk,
    updateLoyaltyConfigThunk,
    LoyaltyState,
    LoyaltyStatus
} from '@spike/loyalty-action';
import { LoyaltyConfigUpdate } from '@spike/loyalty-model';
import { RootState } from 'store';
import { LoyaltyEditLayout, LoyaltySummary, RuleCard } from './UI';
import styles from './Loyalty.module.scss';
import { faPencil } from '@fortawesome/pro-light-svg-icons';
import { buildConfigUpdate, buildEarningRules, buildLoyaltyPointsFields, EditTarget, LoyaltyRule } from './const';
import { formatInt } from './utils';

const Loyalty: FunctionComponent = () => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const hasPremiumPlan = useHasPremiumPlan();

    const loyaltySlice = useSelector<RootState, LoyaltyState>(state => state.loyalty);
    const config = loyaltySlice.config;
    const isLoading = loyaltySlice.status === LoyaltyStatus.Fetching && !config;

    const summary = loyaltySlice.summary;
    const summaryLoading =
        loyaltySlice.summaryStatus === LoyaltyStatus.Initial || loyaltySlice.summaryStatus === LoyaltyStatus.Fetching;

    const [showDisableConfirm, setShowDisableConfirm] = useState(false);
    const [showPremiumAlert, setShowPremiumAlert] = useState(false);
    const [editTarget, setEditTarget] = useState<EditTarget>(null);

    useEffect(() => {
        (dispatch as any)(fetchLoyaltyConfigThunk(apiClientWrapper));
        (dispatch as any)(fetchLoyaltySummaryThunk(apiClientWrapper));
        // Fetch once on mount; apiClientWrapper is recreated every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dispatchUpdate = (update: LoyaltyConfigUpdate) => {
        (dispatch as any)(updateLoyaltyConfigThunk(apiClientWrapper, update));
    };

    const enabled = config?.active ?? false;
    const earningRules = config ? buildEarningRules(config) : [];
    const loyaltyPointsFields = config ? buildLoyaltyPointsFields(config) : [];
    const editingRule = earningRules.find(rule => rule.key === editTarget);

    const handleGlobalToggle = (checked: boolean) => {
        if (!checked) {
            setShowDisableConfirm(true);
            return;
        }

        if (!hasPremiumPlan) {
            setShowPremiumAlert(true);
            return;
        }

        dispatchUpdate({ active: true });
    };

    const confirmDisable = () => {
        dispatchUpdate({ active: false });
        setShowDisableConfirm(false);
    };

    const toggleRule = (rule: LoyaltyRule) => {
        dispatchUpdate({ [rule.enabledField]: !config?.[rule.enabledField] } as LoyaltyConfigUpdate);
    };

    const closeEdit = () => {
        setEditTarget(null);
    };

    const saveLoyaltyPoints = (changes: Record<string, string>) => {
        dispatchUpdate(buildConfigUpdate(changes));
        closeEdit();
    };

    const saveEarningRule = (changes: Record<string, string>) => {
        dispatchUpdate(buildConfigUpdate(changes));
        closeEdit();
    };

    const header = (
        <DefaultHeader
            title="Loyalty"
            className={styles.header}
            leftContentClassName={styles.headerLeft}
            textBelowTitle="View and manage how clients earn and spend points."
            customButton={
                config && (
                    <Switch
                        checked={enabled}
                        aria-label={enabled ? 'Disable Loyalty' : 'Enable Loyalty'}
                        className={styles.switchControl}
                        onChange={handleGlobalToggle}
                    />
                )
            }
        />
    );

    const disabledBanner = config && !enabled && (
        <div
            className={styles.banner}
            role="status"
        >
            <FontAwesomeIcon
                icon={faCircleInfo}
                className={styles.bannerIcon}
            />
            <span>Loyalty is turned off. Enable it to let clients start earning and redeeming points.</span>
        </div>
    );

    const pointsCard = config && (
        <section className={styles.pointsCard}>
            <div className={styles.cardHeader}>
                <h2 className={styles.cardTitle}>Loyalty Points</h2>
                <Button
                    label="Edit"
                    leftIcon={faPencil}
                    variant="text-green"
                    size="inline"
                    className={styles.editButton}
                    disabled={!enabled}
                    onClick={() => setEditTarget('loyalty-points')}
                />
            </div>
            <div className={styles.pointsGrid}>
                <div className={styles.pointsItem}>
                    <span className={styles.pointsIcon}>
                        <FontAwesomeIcon icon={faCalendarDays} />
                    </span>
                    <div>
                        <span className={styles.itemLabel}>Points Hold Period</span>
                        <strong className={styles.itemValue}>{formatInt(config.holdPeriodDays)} Days</strong>
                    </div>
                </div>
                <div className={styles.pointsItem}>
                    <span className={styles.pointsIcon}>
                        <FontAwesomeIcon icon={faMoneyBill} />
                    </span>
                    <div>
                        <span className={styles.itemLabel}>Rate</span>
                        <strong className={styles.itemValue}>
                            {formatInt(config.pointsPerDollarValue)} points = $1
                        </strong>
                    </div>
                </div>
            </div>
        </section>
    );

    const earningSection = config && (
        <section className={styles.earningSection}>
            <DefaultHeader
                title="Ways To Earn Points"
                className={styles.header}
                leftContentClassName={styles.headerLeft}
                textBelowTitle="Set up the ways clients earn points."
            />
            <div className={styles.ruleList}>
                {earningRules.map(rule => (
                    <RuleCard
                        key={rule.key}
                        icon={rule.icon}
                        title={rule.title}
                        description={rule.description}
                        checked={enabled && Boolean(config[rule.enabledField])}
                        disabled={!enabled}
                        onToggle={() => toggleRule(rule)}
                        onEdit={() => setEditTarget(rule.key)}
                    />
                ))}
            </div>
        </section>
    );

    const content = config && (
        <section
            className={clsx(styles.content, {
                [styles.contentDisabled]: !enabled
            })}
            aria-disabled={!enabled}
        >
            {pointsCard}
            {earningSection}
        </section>
    );

    const disableConfirmModal = showDisableConfirm && (
        <GenericModal
            className={styles.modal}
            onClose={() => setShowDisableConfirm(false)}
            footer={
                <div className={styles.modalActions}>
                    <Button
                        label="No"
                        variant="black"
                        fullWidth
                        onClick={() => setShowDisableConfirm(false)}
                    />
                    <Button
                        label="Yes"
                        variant="danger"
                        fullWidth
                        onClick={confirmDisable}
                    />
                </div>
            }
        >
            <div className={styles.modalContent}>
                <h2
                    className={styles.modalTitle}
                    id="disable-loyalty-title"
                >
                    Disable Loyalty?
                </h2>
                <p className={styles.modalDescription}>
                    Clients will no longer earn or use their points if you proceed.
                </p>
            </div>
        </GenericModal>
    );

    const premiumAlert = showPremiumAlert && (
        <PremiumFeatureAlert
            isOpen={showPremiumAlert}
            onCancel={() => setShowPremiumAlert(false)}
            text="Unlock Loyalty with a premium plan"
        />
    );

    const loyaltyPointsEdit = editTarget === 'loyalty-points' && config && (
        <LoyaltyEditLayout
            title="Loyalty Points"
            fields={loyaltyPointsFields}
            onClose={closeEdit}
            onSave={saveLoyaltyPoints}
        />
    );

    const earningRuleEdit = editingRule && (
        <LoyaltyEditLayout
            title="Edit Points"
            contentTitle={editingRule.title}
            description={editingRule.editDescription}
            fields={editingRule.fields}
            onClose={closeEdit}
            onSave={saveEarningRule}
        />
    );

    return (
        <div className={styles.root}>
            {header}
            {disabledBanner}
            <div
                className={clsx(styles.summary, {
                    [styles.contentDisabled]: Boolean(config) && !enabled
                })}
                aria-disabled={Boolean(config) && !enabled}
            >
                <LoyaltySummary
                    totalPointsEarned={summary?.totalPointsEarned ?? 0}
                    pendingPoints={summary?.pendingPoints ?? 0}
                    walletCreditIssued={summary?.walletCreditIssued ?? 0}
                    activeCustomers={summary?.activeCustomers ?? 0}
                    isLoading={summaryLoading}
                />
            </div>
            {isLoading ? (
                <div className={styles.spinnerContainer}>
                    <Spinner />
                </div>
            ) : (
                content
            )}
            {disableConfirmModal}
            {premiumAlert}
            {loyaltyPointsEdit}
            {earningRuleEdit}
        </div>
    );
};

export default Loyalty;
